import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSetRecoilState } from 'recoil'

import { InsuranceCompany, InsurancePayerOption, SupportedLanguage } from '../../__generated__/api'
import { selectedInsuranceAtom, selectedInsurancePayerAtom } from '../../state/common/atoms'
import api from '../services/api'
import { findFallbackOption, findPayerOption } from '../utils/insurance'

import { useApi, useApi2 } from './useApi'

export const useInsurances = (): { insurances: InsuranceCompany[]; pending: boolean } => {
  const { i18n } = useTranslation()

  const { data, pending } = useApi2(
    api.v1.insuranceCompanies,
    {
      lang: i18n.language as SupportedLanguage,
    },
    []
  )

  return { insurances: data, pending }
}

export const useInsuranceName = (id: number | null): { name: string; pending: boolean } => {
  const { t } = useTranslation()
  const { insurances, pending } = useInsurances()
  const [name, setName] = useState<string>('')

  useEffect(() => {
    setName(
      [...insurances, { id: 'other', name: t('common.otherInsurance') }].find(
        (insurance) => insurance.id === id
      )?.name ?? ''
    )
  }, [insurances, id, t])

  return { name, pending }
}

export const useInsurancePayerOptions = (id: number | null | undefined) => {
  const { insurances, pending } = useInsurances()
  const [insurancePayerOptions, setInsurancePayerOptions] = useState<InsurancePayerOption[]>([])
  useEffect(() => {
    if (insurances.length > 0) {
      const selectedInsurance = insurances.find((insurance) => insurance.id === id)
      setInsurancePayerOptions(selectedInsurance?.insurancePayerOptions ?? [])
    }
  }, [insurances, id])

  return { insurancePayerOptions, pending }
}

export const useInsurancePayerOption = (
  contractId: number | null | undefined,
  payerId: string | null
) => {
  const { insurancePayerOptions, pending } = useInsurancePayerOptions(contractId)
  const [insurancePayerOption, setInsurancePayerOption] = useState<InsurancePayerOption>()

  useEffect(() => {
    if (insurancePayerOptions.length > 0) {
      const found = insurancePayerOptions.find((option) => option.id === payerId)
      setInsurancePayerOption(found)
    }
  }, [insurancePayerOptions, payerId])

  return { insurancePayerOption, pending }
}

/**
 * Hook for handling fallback insurance options when primary option is not available
 * @returns Function to find and apply the fallback insurance if available, or continue as self-paying
 */
export const useFallbackInsurance = () => {
  const { insurances } = useInsurances()
  const setSelectedInsurance = useSetRecoilState(selectedInsuranceAtom)
  const setSelectedInsurancePayer = useSetRecoilState(selectedInsurancePayerAtom)

  /**
   * Apply fallback insurance or redirect to self-paying
   */
  const redirectToFallbackInsurance = (
    currentPayerId: string | null,
    onFallbackToSelfPaying: () => void
  ) => {
    // If no insurance payer is selected, use self-paying
    if (!currentPayerId) {
      onFallbackToSelfPaying()
      return
    }

    // Find the current option
    const currentPayerResult = findPayerOption(insurances, currentPayerId)
    if (!currentPayerResult) {
      onFallbackToSelfPaying()
      return
    }

    const { option: currentOption } = currentPayerResult

    // Check if fallback exists
    if (!('fallbackPaymentOption' in currentOption) || !currentOption.fallbackPaymentOption) {
      onFallbackToSelfPaying()
      return
    }

    // Find the fallback option
    const fallbackResult = findFallbackOption(insurances, currentOption.fallbackPaymentOption)
    if (!fallbackResult) {
      onFallbackToSelfPaying()
      return
    }

    // Apply the fallback
    setSelectedInsurance(fallbackResult.companyId)
    setSelectedInsurancePayer(fallbackResult.option.id)
  }

  return { redirectToFallbackInsurance }
}

/**
 * Hook for fetching insurance options data from the API
 * @param subjectOmUid - The OM user ID of the subject
 * @param insuranceCompanyId - The ID of the insurance company
 * @returns Insurance options data and loading state
 */
export const useInsuranceOptions = (
  subjectOmUid: number,
  insuranceCompanyId: string,
  incidentType?: string
) => {
  const { i18n } = useTranslation()
  const currentLanguage = i18n.language as SupportedLanguage

  const { data, pending, error } = useApi(
    api.v1.insuranceOptions,
    {
      subjectOmUid,
      insuranceCompanyId,
      lang: currentLanguage,
      incidentType,
    },
    undefined
  )

  return { insuranceOptions: data, pending, error }
}
